import { useState, useEffect } from 'react';
import { doc, collection, getDocs, setDoc, deleteDoc, serverTimestamp, getDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import AircraftForm from './AircraftForm';
import CSVImportHandler from '../../imports/CSVImportHandler';
import './AircraftSection.css';

// Utility function to convert a string to camelCase
const toCamelCase = (str) => {
  return str
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, '');
};

export const AircraftSection = ({ db, onComplete }) => {
  const { user } = useAuth();
  const [aircraft, setAircraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [designators, setDesignators] = useState([]);
  const [selectedDesignator, setSelectedDesignator] = useState('');
  const [hours, setHours] = useState('');
  const [highlightedId, setHighlightedId] = useState(null);
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1906 + 2 }, (_, i) => 1906 + i);

  useEffect(() => {
    const loadAircraft = async () => {
      try {
        const aircraftCollectionRef = collection(db, `pilot/${user.uid}/aircraft`);
        const querySnapshot = await getDocs(aircraftCollectionRef);

        const aircraftList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setAircraft(aircraftList);
      } catch (err) {
        console.error('Error loading aircraft:', err);
        setError('Failed to load aircraft.');
      } finally {
        setLoading(false);
      }
    };

    const loadManufacturers = async () => {
      try {
        const manufacturersCollectionRef = collection(db, 'aircraft_manufacturers');
        const querySnapshot = await getDocs(manufacturersCollectionRef);

        const manufacturersList = querySnapshot.docs
          .map(doc => doc.data().manufacturerName)
          .sort((a, b) => a.localeCompare(b));

        setManufacturers(manufacturersList);
      } catch (err) {
        console.error('Error loading manufacturers:', err);
      }
    };

    if (db && user?.uid) {
      loadAircraft();
      loadManufacturers();
    }
  }, [db, user]);

  useEffect(() => {
    const loadDesignators = async () => {
      if (!selectedManufacturer) return;

      try {
        const aircraftCollectionRef = collection(db, 'aircraft');
        const querySnapshot = await getDocs(aircraftCollectionRef);

        const designatorsList = querySnapshot.docs
          .filter(doc => doc.data().aircraft_manufacturer === selectedManufacturer)
          .map(doc => doc.data().FAA_Designator);

        setDesignators(designatorsList);
      } catch (err) {
        console.error('Error loading designators:', err);
      }
    };

    loadDesignators();
  }, [selectedManufacturer]);

  const updateHoursInType = async (designator) => {
    try {
      // 1. Get all aircraft with matching designator for the user
      const userAircraftRef = collection(db, `pilot/${user.uid}/aircraft`);
      const aircraftSnapshot = await getDocs(userAircraftRef);
      const matchingAircraft = aircraftSnapshot.docs
        .filter(doc => doc.data().typecode === designator)
        .map(doc => doc.data().aircraftid);

      if (matchingAircraft.length === 0) return 0;

      // 2. Get experience entries for matching aircraft
      const experienceRef = collection(db, `pilot/${user.uid}/experience`);
      const experienceSnapshot = await getDocs(experienceRef);
      const matchingExperience = experienceSnapshot.docs
        .filter(doc => matchingAircraft.includes(doc.data().aircraftid))
        .map(doc => doc.data().totaltime || "0");

      // 3. Sum total time (converting strings to numbers)
      const totalHours = matchingExperience.reduce((sum, time) => {
        return sum + parseFloat(time || "0");
      }, 0);

      // 4. Update hours_in_type in pilot document
      const pilotRef = doc(db, 'pilot', user.uid);
      const pilotDoc = await getDoc(pilotRef);
      const currentHoursInType = pilotDoc.data()?.hours_in_type || [];

      // Remove existing entry for this designator if it exists
      const filteredHours = currentHoursInType.filter(entry => entry.designator !== designator);

      // Add updated entry
      const updatedHoursInType = [
        ...filteredHours,
        {
          designator,
          total: totalHours
        }
      ];

      // Update the pilot document
      await updateDoc(pilotRef, {
        hours_in_type: updatedHoursInType
      });

      return totalHours;
    } catch (err) {
      console.error('Error updating hours in type:', err);
      throw err;
    }
  };

  const handleQuickAdd = async () => {
    if (!selectedManufacturer || !selectedDesignator || !hours) {
      setError('Please complete all fields.');
      return;
    }

    try {
      const aircraftCollectionRef = collection(db, 'aircraft');
      const querySnapshot = await getDocs(aircraftCollectionRef);

      // Find the document that matches the selected designator
      const selectedAircraftDoc = querySnapshot.docs.find(
        doc => doc.data().FAA_Designator === selectedDesignator
      );

      if (!selectedAircraftDoc) {
        setError('Selected aircraft not found.');
        return;
      }

      const aircraftData = selectedAircraftDoc.data(); // Define aircraftData

      const newAircraftData = {
        aircraftid: aircraftData.aircraftid || 'N245AT', // Use data from Firestore or default
        category: aircraftData.category || 'airplane',
        class: aircraftData.class || 'airplane_multi_engine_land',
        complex: aircraftData.complex || false,
        createdAt: serverTimestamp(),
        enginetype: aircraftData.enginetype || 'Piston',
        geartype: aircraftData.geartype || 'retractable_tricycle',
        highperformance: aircraftData.highperformance || false,
        make: selectedManufacturer,
        model: aircraftData.model || 'Seminole',
        pressurized: aircraftData.pressurized || false,
        typecode: selectedDesignator,
        updatedAt: serverTimestamp(),
        year: aircraftData.year || '',
        hours: parseInt(hours, 10),
      };

      const pilotAircraftCollectionRef = collection(db, `pilot/${user.uid}/aircraft`);
      const newAircraftRef = doc(pilotAircraftCollectionRef);

      await setDoc(newAircraftRef, newAircraftData);

      // Update hours in type after adding new aircraft
      await updateHoursInType(selectedDesignator);

      // Add new aircraft to the top of the list and highlight it
      setAircraft(prev => [{ ...newAircraftData, id: newAircraftRef.id }, ...prev]);
      setHighlightedId(newAircraftRef.id);

      // Remove highlight after 2 seconds
      setTimeout(() => setHighlightedId(null), 2000);

      setSelectedManufacturer('');
      setSelectedDesignator('');
      setHours('');
      onComplete?.();
    } catch (err) {
      console.error('Error adding aircraft:', err);
      setError('Failed to add aircraft.');
    }
  };

  const handleSave = async (formData) => {
    try {
      const aircraftCollectionRef = collection(db, `pilot/${user.uid}/aircraft`);
      const docRef = editingId ? doc(aircraftCollectionRef, editingId) : doc(aircraftCollectionRef);

      await setDoc(docRef, formData);

      const updatedAircraft = editingId
        ? aircraft.map(ac => (ac.id === editingId ? { ...formData, id: editingId } : ac))
        : [...aircraft, { ...formData, id: docRef.id }];

      setAircraft(updatedAircraft);
      setEditingId(null);
      setIsAdding(false);

      onComplete?.();
    } catch (err) {
      console.error('Error saving aircraft:', err);
      setError('Failed to save aircraft.');
    }
  };

  const handleDelete = async (id) => {
    try {
      const aircraftCollectionRef = collection(db, `pilot/${user.uid}/aircraft`);
      await deleteDoc(doc(aircraftCollectionRef, id));

      const updatedAircraft = aircraft.filter(ac => ac.id !== id);
      setAircraft(updatedAircraft);

      onComplete?.();
    } catch (err) {
      console.error('Error deleting aircraft:', err);
      setError('Failed to delete aircraft.');
    }
  };

  const refreshAircraftData = async () => {
    try {
      const aircraftCollectionRef = collection(db, `pilot/${user.uid}/aircraft`);
      const querySnapshot = await getDocs(aircraftCollectionRef);

      const aircraftList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setAircraft(aircraftList);
    } catch (err) {
      console.error('Error refreshing aircraft data:', err);
    }
  };

  const renderManufacturerOptions = () => {
    // First deduplicate the manufacturers
    const uniqueManufacturers = [...new Set(manufacturers)];
    
    return uniqueManufacturers.map((manufacturer, index) => (
      <option 
        key={`${manufacturer}-${index}`} 
        value={manufacturer}
      >
        {manufacturer}
      </option>
    ));
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="space-y-4">
      {/* Quick Entry Section Label */}
      <div className="text-left">
        <h3 className="text-lg font-semibold">Quick Entry</h3>
      </div>

      {/* Quick Entry Form */}
      <div className="flex space-x-4 items-center">
        <select
          value={selectedManufacturer}
          onChange={(e) => setSelectedManufacturer(e.target.value)}
          className="border rounded-lg px-2 py-1"
        >
          <option value="">Select Manufacturer</option>
          {[...new Set(manufacturers)].map((manufacturer, index) => (
            <option 
              key={`${manufacturer}-${index}`} 
              value={manufacturer}
            >
              {manufacturer}
            </option>
          ))}
        </select>

        {selectedManufacturer && (
          <select
            value={selectedDesignator}
            onChange={(e) => setSelectedDesignator(e.target.value)}
            className="border rounded-lg px-2 py-1"
          >
            <option value="">Select Designator</option>
            {designators
              .sort((a, b) => a.localeCompare(b))
              .map((designator) => (
              <option key={designator} value={designator}>
                {designator}
              </option>
            ))}
          </select>
        )}

        {selectedDesignator && (
          <input
            type="number"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="Hours"
            className="border rounded-lg px-2 py-1 w-[150px]"
          />
        )}

        {selectedDesignator && hours && (
          <button
            onClick={handleQuickAdd}
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
          >
            Add
          </button>
        )}
      </div>

      {/* Bulk Entry Section Label */}
      <div className="text-left mt-4">
        <h3 className="text-lg font-semibold">Bulk Entry</h3>
      </div>

      {/* Top Buttons */}
      <div className="flex justify-between items-center space-x-4">
        <CSVImportHandler 
          db={db}
          userId={user.uid}
          type="aircraft"
          onSuccess={() => onComplete && onComplete()}
          onDataRefresh={refreshAircraftData}
        />
        <button
          onClick={() => setIsAdding((prev) => !prev)}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
        >
          {isAdding ? "Cancel Add" : "Add Aircraft"}
        </button>
      </div>

      {isAdding && (
        <AircraftForm
          onSubmit={handleSave}
          onCancel={() => setIsAdding(false)}
          yearOptions={yearOptions}
        />
      )}

      {/* Aircraft List */}
      {aircraft
        .slice()
        .sort((a, b) => b.createdAt.seconds - a.createdAt.seconds)
        .map(ac => (
          <div
            key={ac.id}
            className={`p-4 border rounded-lg space-y-2 ${highlightedId === ac.id ? 'highlight' : ''}`}
          >
            <div className="flex justify-between items-center">
              <p>
                {ac.year ? `${ac.year} ` : ''}{ac.aircraft_manufacturer} {ac.model} {ac.registration}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingId(ac.id)}
                  className="bg-blue-500 text-white px-2 py-1 rounded-lg hover:bg-blue-600"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(ac.id)}
                  className="bg-red-500 text-white px-2 py-1 rounded-lg hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
            {editingId === ac.id && (
              <AircraftForm
                initialData={ac}
                onSubmit={handleSave}
                onCancel={() => setEditingId(null)}
                yearOptions={yearOptions}
              />
            )}
          </div>
        ))}
    </div>
  );
};

export default AircraftSection;